import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { X } from "@phosphor-icons/react";
import { cn } from "~/lib/utils";
import { Button, Input } from "./primitives";

/* ============================================================================
   Dialog
   ----------------------------------------------------------------------------
   Radix owns the hard parts: focus trap, focus restore, Escape, scroll lock and
   the ARIA wiring. The previous admin hand-rolled its cart and modal overlays as
   plain divs with role="dialog", so keyboard users tabbed straight into the page
   behind them and Escape did nothing.
   ========================================================================= */

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}) {
  const widths = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  } as const;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-[oklch(0_0_0/0.42)]",
            "data-[state=open]:animate-none",
            // A short fade is fine here: it is an infrequent, non-critical
            // transition, and reduced motion collapses it globally.
            "transition-opacity duration-150 ease-[cubic-bezier(0.2,0,0,1)]",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2",
            "glass-overlay flex max-h-[calc(100dvh-4rem)] flex-col",
            "rounded-[var(--admin-radius-card)]",
            widths[size],
          )}
        >
          <header className="flex items-start justify-between gap-4 px-5 pb-3 pt-5">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-base font-semibold text-[var(--cg-content)]">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-[0.8125rem] text-[var(--cg-content-muted)]">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                // Radix warns without a description; silence it explicitly rather
                // than shipping an unlabelled dialog.
                <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close dialog">
                <X className="size-4" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-1">{children}</div>

          {footer && (
            <footer className="flex flex-wrap justify-end gap-2 border-t border-[var(--cg-line)] px-5 py-4">
              {footer}
            </footer>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ============================================================================
   Confirm
   ========================================================================= */

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  onConfirm,
  /**
   * When set, the operator must type this exact string to enable the action.
   * Used for order deletion, which destroys the only record of a real
   * transaction and cannot be undone.
   */
  confirmPhrase,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  confirmPhrase?: string;
}) {
  const [typed, setTyped] = useState("");
  const phraseSatisfied = !confirmPhrase || typed.trim() === confirmPhrase;

  function handleOpenChange(next: boolean) {
    if (!next) setTyped("");
    onOpenChange(next);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            loading={loading}
            disabled={!phraseSatisfied}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="pb-2 text-[0.8125rem] text-[var(--cg-content-muted)]">{description}</div>

      {confirmPhrase && (
        <div className="pb-3">
          <label htmlFor="confirm-phrase" className="mb-1.5 block text-[0.8125rem] font-medium text-[var(--cg-content)]">
            Type <span className="font-mono font-semibold text-[var(--cg-content)]">{confirmPhrase}</span> to confirm
          </label>
          <Input
            id="confirm-phrase"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            autoFocus
            placeholder={confirmPhrase}
          />
        </div>
      )}
    </Dialog>
  );
}

/** Imperative helper so callers do not hand-manage open state for one-off confirms. */
export function useConfirm() {
  const [state, setState] = useState<{
    title: string;
    description: ReactNode;
    confirmLabel?: string;
    destructive?: boolean;
    confirmPhrase?: string;
    resolve?: (value: boolean) => void;
  } | null>(null);

  function confirm(options: Omit<NonNullable<typeof state>, "resolve">): Promise<boolean> {
    return new Promise((resolve) => setState({ ...options, resolve }));
  }

  function settle(value: boolean) {
    state?.resolve?.(value);
    setState(null);
  }

  // If the host unmounts mid-confirm (a route change on a 401, say), settle the
  // promise as "cancelled" so an `await confirm()` cannot hang forever.
  const resolveRef = useRef(state?.resolve);
  resolveRef.current = state?.resolve;
  useEffect(() => () => resolveRef.current?.(false), []);

  const element = state ? (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
      title={state.title}
      description={state.description}
      confirmLabel={state.confirmLabel}
      destructive={state.destructive}
      confirmPhrase={state.confirmPhrase}
      onConfirm={() => settle(true)}
    />
  ) : null;

  return { confirm, confirmElement: element };
}

/* ============================================================================
   Dropdown menu
   ========================================================================= */

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

export function DropdownMenuContent({
  children,
  align = "end",
  className,
}: {
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
}) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn(
          "glass-overlay z-50 min-w-44 overflow-hidden rounded-[var(--admin-radius-inner)] p-1",
          className,
        )}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

export function DropdownMenuItem({
  children,
  onSelect,
  destructive = false,
  disabled = false,
  icon,
  asChild = false,
}: {
  children: ReactNode;
  onSelect?: () => void;
  destructive?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  /** Render as the child element, so a router `<Link>` keeps its own semantics. */
  asChild?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Item
      asChild={asChild}
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        "flex cursor-pointer select-none items-center gap-2 rounded-[var(--admin-radius-chip)] px-2.5 py-1.5",
        "text-[0.8125rem] outline-none",
        // Radix sets data-highlighted for pointer AND keyboard, so hover and
        // keyboard focus get the same treatment.
        "data-[highlighted]:bg-[color-mix(in_oklab,var(--cg-content)_8%,transparent)]",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        destructive
          ? "text-[var(--cg-danger)] data-[highlighted]:bg-[var(--admin-danger-tint)]"
          : "text-[var(--cg-content)]",
      )}
    >
      {asChild ? (
        children
      ) : (
        <>
          {icon && <span className="shrink-0 [&>svg]:size-4">{icon}</span>}
          {children}
        </>
      )}
    </DropdownMenuPrimitive.Item>
  );
}

export function DropdownMenuSeparator() {
  return <DropdownMenuPrimitive.Separator className="my-1 h-px bg-[var(--cg-line)]" />;
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenuPrimitive.Label className="px-2.5 py-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--cg-content-subtle)]">
      {children}
    </DropdownMenuPrimitive.Label>
  );
}

/* ============================================================================
   Tooltip
   ========================================================================= */

const TooltipCtx = createContext(false);

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={350} skipDelayDuration={300}>
      <TooltipCtx.Provider value={true}>{children}</TooltipCtx.Provider>
    </TooltipPrimitive.Provider>
  );
}

/**
 * Tooltips are supplementary only. Every control that uses one also carries an
 * accessible name, so nothing is discoverable ONLY by hovering.
 */
export function Tooltip({ label, children, side = "bottom" }: { label: string; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  const provided = useContext(TooltipCtx);
  if (!provided) return <>{children}</>;

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 rounded-[var(--admin-radius-chip)] bg-[var(--color-soil-900)] px-2 py-1 text-xs font-medium text-[var(--color-paper-100)] shadow-[var(--admin-shadow-pop)] dark:bg-[var(--color-soil-100)] dark:text-[var(--color-soil-900)]"
        >
          {label}
          <TooltipPrimitive.Arrow className="fill-[var(--color-soil-900)] dark:fill-[var(--color-soil-100)]" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
